import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
    useDeleteSubscriptionMutation,
    useGetShareSubcategoriesQuery,
    useReopenSubscriptionMutation,
} from 'api/main';
import { signatureDialogSet } from 'reducers/user';
import {
    getAlertTypeForUser,
    getShareSubcategoryIdFromIRI,
    parseDate,
    parseFullName,
    parseSum,
} from 'utils/helpers';
import { RoleNames, providerRoles, promoterRoles } from 'utils/constants';
import {
    useMatchAtLeastOneRole,
    useSubscriptionCanBeSignedByProvider,
    useSubscriptionFormSignatureStatus,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import {
    MenuDelete,
    MenuDetails,
    MenuReopen,
    MenuSendMessage,
    MenuSendSMS,
    MenuSignBS,
    MenuSubscriptionMove,
    MenuSubscriptionTransfer,
    MenuSubstitute,
} from 'components/ActionsMenu/actions';
import {
    ActionsMenu,
    ConfirmDialog,
    SMSDialog,
    StyledTableRow,
    SubscriptionAlertCircle,
} from 'components';
import { TableCell, Typography } from '@mui/material';

const ImpersonateItem = React.forwardRef((props, ref) => <MenuSubstitute ref={ref} {...props} />);
const DeleteSubscriptionItem = React.forwardRef((props, ref) => (
    <MenuDelete ref={ref} {...props} />
));
const MessageItem = React.forwardRef((props, ref) => <MenuSendMessage ref={ref} {...props} />);
const SMSItem = React.forwardRef((props, ref) => <MenuSendSMS ref={ref} {...props} />);

const SubscriptionRow = ({ subscription }) => {
    const dispatch = useDispatch();

    const { data: shareSubcategories = [] } = useGetShareSubcategoriesQuery();

    const isAdminOrSignatory = useMatchAtLeastOneRole([
        RoleNames.SERVICER_SIGNATORY,
        RoleNames.SERVICER_ADMIN,
    ]);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const userIsProvider = useMatchAtLeastOneRole(providerRoles);
    const userIsPromoter = useMatchAtLeastOneRole(promoterRoles);
    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);
    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsAdminCIF = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF, RoleNames.PSI]);
    const userIsCIF = useMatchAtLeastOneRole([RoleNames.COMMERCIALISATEUR]);
    const [deleteSubscription, { isSuccess: deleteSuccessful, isLoading: deleteLoader }] =
        useDeleteSubscriptionMutation();
    const [reopenSubscription, { isSuccess: reopenSuccess, isLoading: reopenLoader }] =
        useReopenSubscriptionMutation();
    const [smsOpen, setSmsOpen] = useState(false);
    const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const handleSMSOpen = useCallback(() => setSmsOpen(true), []);
    const handleReopenSubscriptionOpen = useCallback(() => setReopenDialogOpen(true), []);
    const handleOpenSignatureChoice = useCallback(
        () => dispatch(signatureDialogSet({ open: true, subscription })),
        [subscription, dispatch]
    );
    const handleOpenDeleteDialog = useCallback(() => setDeleteDialogOpen(true), []);

    const handleConfirmReopen = useCallback(
        () => reopenSubscription(subscription.id),
        [reopenSubscription, subscription]
    );

    const handleDeleteSubscription = useCallback(
        () => deleteSubscription({ id: subscription.id }),
        [deleteSubscription, subscription]
    );

    const alertType = useMemo(() => getAlertTypeForUser(subscription), [subscription]);
    const canSignBS = useSubscriptionCanBeSignedByProvider(subscription);
    const hasSignedBS = useSubscriptionFormSignatureStatus(subscription);
    const shareSubCategoryId = useMemo(
        () =>
            getShareSubcategoryIdFromIRI(
                subscription?.investmentCharacteristics?.shareSubcategory || '-1'
            ),
        [subscription]
    );
    const shareSubcategory = useMemo(
        () => shareSubcategories?.find(subCategory => subCategory.id === shareSubCategoryId),
        [shareSubcategories, shareSubCategoryId]
    );
    const date = useMemo(() => parseDate(subscription.createdAt), [subscription]);
    const profile = useMemo(
        () => (subscription.role?.name?.includes('PP') ? 'PP' : 'PM'),
        [subscription]
    );

    const isIndirectSubscription = subscription.indirectUntransferred;
    const canSubstitute =
        (((userIsCIF || userIsAdminCIF || isAdminOrSignatory) && isIndirectSubscription) ||
            subscription.user?.emailConfirmed) &&
        !userIsNetworkAdmin &&
        !userIsPromoter &&
        !userIsStandardServicer &&
        !userIsProvider;
    const admissionFee =
        typeof subscription.investmentCharacteristics?.admissionFee === 'undefined' ||
        !subscription.investmentCharacteristics ||
        (subscription.indirectUntransferred && !subscription.transferredToInvestorAt)
            ? null
            : subscription.investmentCharacteristics?.admissionFee;
    const maxAdmissionFee = subscription.investmentFund?.maximumAdmissionFee || 0;

    useEffect(() => {
        if (deleteSuccessful) {
            setDeleteDialogOpen(false);
        }
    }, [deleteSuccessful]);

    useEffect(() => {
        if (reopenSuccess) {
            setReopenDialogOpen(false);
        }
    }, [reopenSuccess]);

    return (
        <StyledTableRow key={subscription.id}>
            <TableCell align="center">
                <SubscriptionAlertCircle alertType={alertType} />
            </TableCell>
            <TableCell sx={theme => ({ color: theme.palette.common.black })}>
                {profile === 'PP' ? (
                    <React.Fragment>
                        <Typography gutterBottom fontSize="inherit">
                            {parseFullName(subscription.user)}
                        </Typography>
                        {subscription.coSubscriber ? (
                            <Typography fontSize="inherit">
                                {parseFullName(subscription.coSubscriber)}
                            </Typography>
                        ) : (
                            ''
                        )}
                    </React.Fragment>
                ) : (
                    subscription.user?.investorCompany?.corporateName
                )}
            </TableCell>
            <TableCell>{profile}</TableCell>
            <TableCell>{date || ''}</TableCell>
            <TableCell sx={theme => ({ color: theme.palette.common.black })}>
                <b>{parseSum(subscription?.investmentCharacteristics?.investingSum)}</b>
            </TableCell>
            <TableCell>{admissionFee === null ? '-' : `${admissionFee}\xa0%`}</TableCell>
            <TableCell>{subscription.investmentFund?.name || ''}</TableCell>
            <TableCell align="center">{shareSubcategory?.name || ''}</TableCell>
            <TableCell>{subscription.userFriendlyStatus}</TableCell>
            {!userIsDistributor && (
                <TableCell align="center">
                    {subscription?.distributorCompany?.corporateName ||
                        subscription?.investmentFund?.sgp?.name}
                </TableCell>
            )}
            {userIsDistributor && (
                <TableCell>
                    {subscription?.createdBy ? parseFullName(subscription.createdBy) : '-'}
                </TableCell>
            )}
            <TableCell>
                <ActionsMenu autoClose>
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsPromoter &&
                        !userIsStandardServicer && (
                            <DeleteSubscriptionItem onClick={handleOpenDeleteDialog} />
                        )}
                    {canSubstitute && (
                        <ImpersonateItem
                            user={subscription.user}
                            originalPath="/subscriptions"
                            connectAs="subscriber"
                            followupPath={
                                userIsDistributor && subscription.user?.subscriptions?.length > 1
                                    ? '/new-subscription'
                                    : null
                            }
                            itemText={
                                userIsDistributor
                                    ? texts.CONTINUE_SUBSCRIPTION
                                    : texts.IMPERSONATE_SUBSCRIBER
                            }
                        />
                    )}
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsPromoter &&
                        !userIsStandardServicer && <MessageItem receiver={subscription.user} />}
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsPromoter &&
                        !userIsStandardServicer && (
                            <SMSItem
                                disabled={!subscription.user.phoneNumber}
                                onSendSMS={handleSMSOpen}
                                id={subscription.user.id}
                            />
                        )}
                    <MenuDetails path={`details/${subscription.user.id}/${subscription.id}`} />
                    {hasSignedBS && isAdminOrSignatory && (
                        <MenuReopen onClick={handleReopenSubscriptionOpen} />
                    )}
                    {canSignBS && <MenuSignBS onClick={handleOpenSignatureChoice} />}
                    {userIsDistributor && isIndirectSubscription && (
                        <MenuSubscriptionTransfer
                            maxAdmissionFee={maxAdmissionFee}
                            subscriptionId={subscription.id}
                        />
                    )}
                    {userIsAdminCIF && <MenuSubscriptionMove subscriptionId={subscription.id} />}
                </ActionsMenu>
            </TableCell>
            <SMSDialog
                open={smsOpen}
                handleClose={() => setSmsOpen(false)}
                receiver={subscription.user}
            />
            <ConfirmDialog
                title={texts.REOPEN_TUNNEL}
                open={reopenDialogOpen}
                handleClose={() => setReopenDialogOpen(false)}
                loading={reopenLoader}
                handleConfirm={handleConfirmReopen}
            >
                {texts.CONFIRM_REOPEN_TUNNEL}
            </ConfirmDialog>
            <ConfirmDialog
                open={deleteDialogOpen}
                title={texts.SUPPRIMER}
                loading={deleteLoader}
                handleClose={() => setDeleteDialogOpen(false)}
                handleConfirm={handleDeleteSubscription}
            >
                {texts.ETES_VOUS_SURS_DE_VOULOIR_SUPPRIMER}
            </ConfirmDialog>
        </StyledTableRow>
    );
};

SubscriptionRow.propTypes = {
    subscription: PropTypes.object.isRequired,
};

export default SubscriptionRow;
